import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, Plus, Edit, Trash2, X, Layout, Grid3X3 } from 'lucide-react';
import { api } from '@/lib/api';
import type { CinemaScreen } from '@/types';

const screenSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  sortOrder: z.number(),
});

type ScreenFormData = z.infer<typeof screenSchema>;

export default function ScreensList() {
  const { venueId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editingScreen, setEditingScreen] = useState<CinemaScreen | null>(null);

  const { data: venue } = useQuery({
    queryKey: ['venue', venueId],
    queryFn: async () => {
      const response = await api.get(`/admin/venues/${venueId}`);
      return response.data;
    },
  });

  const { data: screens, isLoading } = useQuery<CinemaScreen[]>({
    queryKey: ['cinema-screens', venueId],
    queryFn: async () => {
      const response = await api.get(`/admin/venues/${venueId}/cinema/screens`);
      return response.data;
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ScreenFormData>({
    resolver: zodResolver(screenSchema),
    defaultValues: {
      name: '',
      description: '',
      sortOrder: 0,
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: ScreenFormData) => {
      await api.post(`/admin/venues/${venueId}/cinema/screens`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cinema-screens', venueId] });
      closeModal();
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: ScreenFormData) => {
      await api.put(`/admin/venues/${venueId}/cinema/screens/${editingScreen?.id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cinema-screens', venueId] });
      closeModal();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (screenId: string) => {
      await api.delete(`/admin/venues/${venueId}/cinema/screens/${screenId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cinema-screens', venueId] });
    },
  });

  const openModal = (screen?: CinemaScreen) => {
    if (screen) {
      setEditingScreen(screen);
      reset({
        name: screen.name,
        description: screen.description || '',
        sortOrder: screen.sort_order,
      });
    } else {
      setEditingScreen(null);
      reset({
        name: '',
        description: '',
        sortOrder: (screens?.length || 0) + 1,
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingScreen(null);
    reset();
  };

  const onSubmit = (data: ScreenFormData) => {
    if (editingScreen) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate(`/venues/${venueId}/cinema`)}
          className="p-2 hover:bg-gray-100 rounded-md"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">Screens</h1>
          <p className="text-sm text-gray-500">
            {(venue as Record<string, unknown>)?.name as string} - Configure cinema screens
          </p>
        </div>
        <button onClick={() => openModal()} className="btn btn-primary">
          <Plus className="h-4 w-4 mr-2" />
          Add Screen
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {isLoading ? (
          [...Array(3)].map((_, i) => (
            <div key={i} className="card p-6 animate-pulse">
              <div className="h-6 bg-gray-200 rounded w-32 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-48 mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-24"></div>
            </div>
          ))
        ) : screens?.length === 0 ? (
          <div className="col-span-full card p-12 text-center text-gray-500">
            No screens configured. Add a screen like "Screen 1", "IMAX" etc.
          </div>
        ) : (
          screens?.map((screen) => (
            <div key={screen.id} className="card p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Layout className="h-6 w-6 text-blue-600" />
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => openModal(screen)}
                    className="p-2 rounded-md hover:bg-gray-100 text-gray-500 hover:text-gray-700"
                    title="Edit"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => {
                      if (confirm('Delete this screen and all its sections?')) {
                        deleteMutation.mutate(screen.id);
                      }
                    }}
                    className="p-2 rounded-md hover:bg-gray-100 text-red-500 hover:text-red-700"
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <h3 className="text-lg font-semibold text-gray-900 mb-1">{screen.name}</h3>
              {screen.description && (
                <p className="text-sm text-gray-500 mb-3">{screen.description}</p>
              )}

              <div className="flex items-center gap-3 mb-4 text-sm">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                  {screen.section_count || 0} sections
                </span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {screen.calculated_seats || screen.total_seats || 0} seats
                </span>
              </div>

              <div className="flex gap-2">
                <Link
                  to={`/venues/${venueId}/cinema/screens/${screen.id}/sections`}
                  className="btn btn-secondary flex-1 justify-center"
                >
                  <Grid3X3 className="h-4 w-4 mr-2" />
                  Sections
                </Link>
                <Link
                  to={`/venues/${venueId}/cinema/screens/${screen.id}/layout`}
                  className="btn btn-primary flex-1 justify-center"
                >
                  <Layout className="h-4 w-4 mr-2" />
                  Layout
                </Link>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">
                {editingScreen ? 'Edit Screen' : 'Add Screen'}
              </h2>
              <button onClick={closeModal} className="p-1 hover:bg-gray-100 rounded">
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Screen Name *
                </label>
                <input
                  type="text"
                  {...register('name')}
                  className="input"
                  placeholder="e.g., Screen 1, IMAX, VIP Screen"
                />
                {errors.name && (
                  <p className="text-sm text-red-600 mt-1">{errors.name.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  {...register('description')}
                  className="input"
                  rows={2}
                  placeholder="Optional description"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sort Order
                </label>
                <input
                  type="number"
                  {...register('sortOrder', { valueAsNumber: true })}
                  className="input"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={closeModal} className="btn btn-secondary">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="btn btn-primary"
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? 'Saving...'
                    : editingScreen
                    ? 'Update'
                    : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
